<template>
  <CreateMediaspaceAdSidebar
    v-model:isVisible="createMediaspaceAdSidebarIsVisible"
    @onCreate="createEntity"
    :creating="creating"
  />

  <UpdateMediaspaceAdSidebar
    v-model:isVisible="updateMediaspaceAdSidebarIsVisible"
    @onUpdate="updateEntity"
    :updating="creating"
    :entity="selectedEntity"
  />

  <div class="page-container">
    <div class="max-w-7xl mx-auto p-6">
      <!-- Header Section -->
      <div class="bg-white border-round-2xl shadow-1 p-6 mb-6">
        <div class="flex align-items-center justify-content-between">
          <div class="flex align-items-center gap-4">
            <div class="header-icon-container">
              <i class="fa fa-ad text-white text-2xl"></i>
            </div>
            <div>
              <h1 class="font-bold text-gray-800 text-3xl m-0 mb-2">{{ pageTitle }}</h1>
              <p class="text-gray-600 m-0 text-lg">{{ pageDescription }}</p>
            </div>
          </div>
          <Button
            @click="createMediaspaceAdSidebarIsVisible = !createMediaspaceAdSidebarIsVisible"
            label="New Ad"
            icon="fa fa-plus"
            class="create-button"
          />
        </div>
      </div>

      <Loading
        v-if="loading"
        :full="false"
        :title="'Loading ' + pageTitle + '...'"
      />

      <!-- Main Content -->
      <div v-else class="bg-white border-round-2xl shadow-1 overflow-hidden">
        <!-- Table Header -->
        <div class="table-header-section">
          <div class="flex align-items-center gap-3">
            <div class="bg-blue-100 p-2 border-round-lg">
              <i class="fa fa-list text-blue-600"></i>
            </div>
            <h2 class="text-xl font-semibold text-gray-800 m-0">Mediaspace Ads Overview ({{ mediaspaceAds.length }})</h2>
            <div class="ml-auto">
              <small class="text-gray-500">
                <i class="fa fa-info-circle mr-1"></i>
                Drag and drop to reorder ads
              </small>
            </div>
          </div>
        </div>

        <!-- Statistics Header -->
        <div class="statistics-header-section">
          <div class="flex align-items-center gap-3">
            <div class="bg-green-100 p-2 border-round-lg">
              <i class="fa fa-chart-bar text-green-600"></i>
            </div>
            <h3 class="text-lg font-medium text-gray-700 m-0">Performance Statistics</h3>
            <div class="ml-auto">
              <small class="text-gray-500">
                <i v-if="statisticsLoading" class="fa fa-spinner fa-spin mr-1"></i>
                <i v-else class="fa fa-refresh mr-1"></i>
                {{ statisticsLoading ? 'Loading statistics...' : 'Auto-refreshed with ad data' }}
              </small>
            </div>
          </div>
        </div>

        <draggable
          v-model="mediaspaceAds"
          @end="onDragEnd"
          item-key="id"
          class="modern-datatable"
          :animation="200"
          ghost-class="ghost-row"
          chosen-class="chosen-row"
        >
          <template #item="{ element: data }">
            <div class="datatable-row">
              <div class="row-content">
                <!-- Drag Handle -->
                <div class="drag-handle">
                  <i class="fa fa-grip-vertical text-gray-400"></i>
                </div>

                <!-- Title -->
                <div class="title-cell">
                  <div class="flex align-items-center gap-3">
                    <div class="ad-video-container">
                      <video 
                        v-if="data.coverImageUrl" 
                        :src="data.coverImageUrl" 
                        class="ad-video"
                        muted
                        loop
                        playsinline
                        preload="auto"
                        @loadeddata="onVideoLoaded"
                        @mouseenter="$event.target.play()"
                        @mouseleave="$event.target.pause()"
                      />
                      <div v-else class="ad-video-placeholder">
                        <i class="fa fa-video text-gray-400"></i>
                      </div>
                    </div>
                    <div>
                      <h4 class="font-semibold text-gray-800 m-0 mb-1">{{ data.backofficeTitle }}</h4>
                      <p class="text-gray-500 text-sm m-0">{{ getDomainFromUrl(data.targetUrl) || 'No target URL' }}</p>
                    </div>
                  </div>
                </div>

                <!-- Locations -->
                <div class="locations-cell">
                  <div class="flex flex-wrap gap-1">
                    <div 
                      v-for="location in data.locations" 
                      :key="location"
                      class="location-badge"
                    >
                      <i class="fa fa-map-marker text-sm"></i>
                      <span>{{ getLocationTitle(location) }}</span>
                    </div>
                  </div>
                </div>

                <!-- Target -->
                <div class="target-cell">
                  <div class="flex">
                    <div class="status-badge target-badge">
                      <i class="fa fa-bullseye text-sm"></i>
                      <span>{{ getTargetTypeTitle(data.targetType) }}</span>
                    </div>
                  </div>
                </div>

                <!-- Display Time -->
                <div class="display-time-cell">
                  <div class="flex">
                    <div class="time-badge">
                      <i class="fa fa-clock text-sm"></i>
                      <span>{{ data.displayTimeSeconds || 5 }}s</span>
                    </div>
                  </div>
                </div>

                <!-- Statistics -->
                <div class="statistics-cell">
                  <div class="flex flex-column gap-1">
                    <div class="stat-badge shows-badge">
                      <i class="fa fa-eye text-sm"></i>
                      <span>{{ getAdStatistics(data.id)?.showCount || 0 }}</span>
                    </div>
                    <div class="stat-badge clicks-badge">
                      <i class="fa fa-mouse-pointer text-sm"></i>
                      <span>{{ getAdStatistics(data.id)?.clickCount || 0 }}</span>
                    </div>
                    <div class="stat-badge ctr-badge">
                      <i class="fa fa-percentage text-sm"></i>
                      <span>{{ formatClickThroughRate(getAdStatistics(data.id)?.clickThroughRate) }}</span>
                    </div>
                  </div>
                </div>

                <!-- Average View Duration -->
                <div class="avg-duration-cell">
                  <div class="flex">
                    <div class="duration-badge">
                      <i class="fa fa-hourglass-half text-sm"></i>
                      <span>{{ formatDuration(getAdStatistics(data.id)?.averageViewDurationSeconds) }}</span>
                    </div>
                  </div>
                </div>

                <!-- Status -->
                <div class="status-cell">
                  <div class="flex">
                    <div
                      :class="data.enabled ? 'status-enabled' : 'status-disabled'"
                      class="status-badge"
                    >
                      <i :class="data.enabled ? 'fa fa-check-circle' : 'fa fa-times-circle'" class="text-sm"></i>
                      <span>{{ data.enabled ? 'Enabled' : 'Disabled' }}</span>
                    </div>
                  </div>
                </div>

                <!-- Actions -->
                <div class="actions-cell">
                  <div class="flex gap-2">
                    <Button
                      @click="showUpdateSidebar(data)"
                      type="button"
                      icon="fa fa-edit"
                      class="action-button edit-button"
                      size="small"
                      v-tooltip.top="'Edit Ad'"
                    />
                    <Button
                      @click="confirmDelete(data)"
                      type="button"
                      icon="fa fa-trash"
                      class="action-button delete-button"
                      size="small"
                      v-tooltip.top="'Delete Ad'"
                    />
                  </div>
                </div>
              </div>
            </div>
          </template>
        </draggable>

        <!-- Empty State -->
        <div v-if="mediaspaceAds.length === 0" class="text-center py-8">
          <i class="fa fa-inbox text-gray-400 text-4xl mb-3"></i>
          <p class="text-gray-500 text-lg m-0">No mediaspace ads found</p>
          <p class="text-gray-400 text-sm m-0 mt-2">Create your first ad to get started</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useToast } from "primevue/usetoast";
import { onMounted, ref, computed } from "vue";
import { useStore } from "vuex";
import { uploadFile } from "@/services/fileUploadService";
import draggable from 'vuedraggable';
import CreateMediaspaceAdSidebar from "./components/CreateMediaspaceAdSidebar.vue";
import UpdateMediaspaceAdSidebar from "./components/UpdateMediaspaceAdSidebar.vue";

export default {
  inject: ["hasPermission"],
  components: { CreateMediaspaceAdSidebar, UpdateMediaspaceAdSidebar, draggable },
  setup() {
    const storeActionGetEntities = "mediaspace/getMediaspaceAdListItems";
    const storeGetterGetEntities = "mediaspace/mediaspaceAdListItems";
    const storeActionEnsureEntities = "mediaspace/ensureMediaspaceAdListItems";
    const storeActionCreateEntity = "mediaspace/create";
    const storeActionUpdateEntity = "mediaspace/update";
    const storeActionDeleteEntity = "mediaspace/delete";
    const storeActionReorderEntities = "mediaspace/reorder";
    const storeActionGetStatistics = "mediaspace/getStatistics";
    const storeGetterStatistics = "mediaspace/statistics";
    const storeGetterStatisticsLoading = "mediaspace/statisticsLoading";

    const pageTitle = "Mediaspace Ads";
    const pageDescription = "Manage your mediaspace advertisements and configure targeting settings";
    const loading = ref(false);
    const creating = ref(false);

    const createMediaspaceAdSidebarIsVisible = ref(false);
    const updateMediaspaceAdSidebarIsVisible = ref(false);

    const store = useStore();
    const toast = useToast();
    const mediaspaceAds = ref([]);
    const selectedEntity = ref();

    // Computed property to sort ads by index
    const sortedMediaspaceAds = computed({
      get: () => {
        return [...mediaspaceAds.value].sort((a, b) => (a.index || 0) - (b.index || 0));
      },
      set: (value) => {
        mediaspaceAds.value = value;
      }
    });

    // Computed properties for statistics
    const statisticsLoading = computed(() => store.getters[storeGetterStatisticsLoading]);
    const statistics = computed(() => store.getters[storeGetterStatistics]);

    function showUpdateSidebar(ad) {
      selectedEntity.value = ad;
      updateMediaspaceAdSidebarIsVisible.value = true;
    }

    function confirmDelete(ad) {
      selectedEntity.value = ad;
      // Use browser's built-in confirm dialog for simplicity
      if (confirm(`Are you sure you want to delete "${ad.backofficeTitle}"? This action cannot be undone.`)) {
        deleteEntity(ad.id);
      }
    }

    function getLocationTitle(location) {
      const locationTitles = {
        'Home': 'Home',
        'Benefits': 'Benefits',
        'Explore': 'Explore'
      };
      return locationTitles[location] || location;
    }

    function getTargetTypeTitle(targetType) {
      const targetTypeTitles = {
        'All': 'All Users',
        'FreeOnly': 'Free Only',
        'SubscribersOnly': 'Subscribers Only'
      };
      return targetTypeTitles[targetType] || targetType;
    }

    function getLocationDisplay(location) {
      if (!location) return null;
      
      const parts = [];
      if (location.street) parts.push(location.street);
      if (location.city) parts.push(location.city);
      if (location.postalCode) parts.push(location.postalCode);
      if (location.country) parts.push(location.country);
      
      return parts.length > 0 ? parts.join(', ') : 'Location set';
    }

    function getLocalizedText(localizedText, languageCode = 'en') {
      if (!localizedText) return null;
      
      // Handle both API format (with translations array) and direct format
      if (localizedText.translations) {
        const translation = localizedText.translations.find(t => t.languageCode === languageCode);
        return translation?.text || null;
      }
      
      // Handle direct format (e.g., { en: "text", da: "text" })
      return localizedText[languageCode] || null;
    }

    function getDomainFromUrl(url) {
      if (!url) return null;
      
      // If url is already a string, use it directly
      const urlString = typeof url === 'string' ? url : getLocalizedText(url, 'en');
      if (!urlString) return null;
      
      try {
        // Add protocol if missing to make URL parsing work
        const urlWithProtocol = urlString.startsWith('http://') || urlString.startsWith('https://') 
          ? urlString 
          : `https://${urlString}`;
        
        // Create a URL object to extract the domain
        const urlObj = new URL(urlWithProtocol);
        // Remove 'www.' if present
        return urlObj.hostname.replace(/^www\./, '');
      } catch (e) {
        // If URL parsing fails, try to extract domain manually using regex
        const match = urlString.match(/(?:https?:\/\/)?(?:www\.)?([^/?#]+)/);
        return match ? match[1].replace(/^www\./, '') : urlString;
      }
    }

    function getAdStatistics(adId) {
      return statistics.value.find(stat => stat.adId === adId);
    }

    function formatClickThroughRate(ctr) {
      if (!ctr || ctr === 0) return '0%';
      return `${ctr.toFixed(1)}%`;
    }

    function formatDuration(seconds) {
      if (!seconds || seconds === 0) return '0s';
      return `${seconds.toFixed(1)}s`;
    }

    function onVideoLoaded(event) {
      // Force Safari to show the first frame by seeking to the beginning
      // This ensures the video preview shows immediately instead of being black
      const video = event.target;
      if (video.currentTime === 0 && video.readyState >= 2) {
        // Video is loaded, ensure it shows the first frame
        video.currentTime = 0.1;
      }
    }

    async function onDragEnd(evt) {
      const { oldIndex, newIndex } = evt;
      
      if (oldIndex === newIndex) return;

      // Store the original order in case we need to revert
      const originalOrder = [...mediaspaceAds.value];

      // Update indices for all items
      const updatedAds = mediaspaceAds.value.map((ad, index) => ({
        ...ad,
        index: index + 1
      }));

      // Prepare reorder data
      const reorderData = {
        ads: updatedAds.map(ad => ({
          id: ad.id,
          index: ad.index
        }))
      };

      try {
        await store.dispatch(storeActionReorderEntities, reorderData);
        
        // Update the local state with the new order
        mediaspaceAds.value = updatedAds;
        
        toast.add({
          severity: "success",
          summary: "Success",
          detail: "Ad order updated successfully",
          life: 3000,
        });
      } catch (err) {
        // Revert to original order on error
        mediaspaceAds.value = originalOrder;
        
        toast.add({
          severity: "error",
          summary: "Error",
          detail: "Could not update ad order: " + err,
          life: 3000,
        });
      }
    }

    async function loadMediaspaceAds() {
      await store
        .dispatch(storeActionEnsureEntities)
        .then(() => {
          const loadedEntities = store.getters[storeGetterGetEntities];
          mediaspaceAds.value = loadedEntities;
        })
        .catch((err) => {
          toast.add({
            severity: "error",
            summary: "Error",
            detail: "Could not get mediaspace ads" + ": " + err,
            life: 3000,
          });
        });
    }

    async function loadRequiredData() {
      loading.value = true;

      await loadMediaspaceAds();
      await loadStatistics();
      
      loading.value = false;
    }

    async function reloadData() {
      loading.value = true;
      try {
        await store.dispatch(storeActionGetEntities);
        const loadedEntities = store.getters[storeGetterGetEntities];
        mediaspaceAds.value = loadedEntities;
      } catch (err) {
        toast.add({
          severity: "error",
          summary: "Error",
          detail: "Could not get mediaspace ads" + ": " + err,
          life: 3000,
        });
      }
      
      await loadStatistics();
      loading.value = false;
    }

    async function loadStatistics() {
      try {
        await store.dispatch(storeActionGetStatistics);
      } catch (err) {
        console.warn("Could not load statistics:", err);
        // Don't show error toast for statistics as it's not critical
      }
    }

    async function handleVideoUpload(formContext) {
      if (formContext.coverImageUrl === selectedEntity.value?.coverImageUrl)
        return formContext.coverImageUrl;

      var videoContainer = "mediaspace";
      var videoUrl = "";
      if (formContext.coverImageUrl) {
        var videoFile = await uploadFile(
          formContext.coverImageUrl,
          "mediaspace",
          videoContainer,
          store
        );
        videoUrl = videoFile.url;
      }
      return videoUrl;
    }

    async function createEntity(formContext) {
      creating.value = true;

      var videoUrl = await handleVideoUpload(formContext);
      formContext.coverImageUrl = videoUrl;

      // Set index to be the next available index
      const maxIndex = mediaspaceAds.value.length > 0 
        ? Math.max(...mediaspaceAds.value.map(ad => ad.index || 0))
        : 0;
      formContext.index = maxIndex + 1;

      await store
        .dispatch(storeActionCreateEntity, formContext)
        .then(() => {
          toast.add({
            severity: "success",
            summary: "Success",
            detail: "Mediaspace ad created",
            life: 3000,
          });
          createMediaspaceAdSidebarIsVisible.value = false;
        })
        .catch((err) => {
          toast.add({
            severity: "error",
            summary: "Error",
            detail: "Could not create mediaspace ad" + ": " + err,
            life: 3000,
          });
        })
        .finally(() => (creating.value = false));

      reloadData();
    }

    async function updateEntity(formContext) {
      creating.value = true;

      var videoUrl = await handleVideoUpload(formContext);
      formContext.coverImageUrl = videoUrl;

      await store
        .dispatch(storeActionUpdateEntity, {
          ...formContext,
          id: selectedEntity.value?.id,
        })
        .then(() => {
          toast.add({
            severity: "success",
            summary: "Success",
            detail: "Mediaspace ad updated",
            life: 3000,
          });
          updateMediaspaceAdSidebarIsVisible.value = false;
        })
        .catch((err) => {
          toast.add({
            severity: "error",
            summary: "Error",
            detail: "Could not update mediaspace ad" + ": " + err,
            life: 3000,
          });
        })
        .finally(() => (creating.value = false));

      reloadData();
    }

    async function deleteEntity(id) {
      creating.value = true;
      await store
        .dispatch(storeActionDeleteEntity, id)
        .then(() => {
          toast.add({
            severity: "success",
            summary: "Success",
            detail: "Mediaspace ad deleted",
            life: 3000,
          });
          updateMediaspaceAdSidebarIsVisible.value = false;
        })
        .catch((err) => {
          toast.add({
            severity: "error",
            summary: "Error",
            detail: "Could not delete mediaspace ad" + ": " + err,
            life: 3000,
          });
        })
        .finally(() => (creating.value = false));

      reloadData();
    }

    onMounted(() => {
      loadRequiredData();
    });

    return {
      pageTitle,
      pageDescription,
      loading,
      mediaspaceAds: sortedMediaspaceAds,
      statisticsLoading,
      createMediaspaceAdSidebarIsVisible,
      updateMediaspaceAdSidebarIsVisible,
      createEntity,
      updateEntity,
      creating,
      showUpdateSidebar,
      selectedEntity,
      getLocationTitle,
      getTargetTypeTitle,
      getLocalizedText,
      getLocationDisplay,
      getDomainFromUrl,
      getAdStatistics,
      formatClickThroughRate,
      formatDuration,
      confirmDelete,
      onDragEnd,
      onVideoLoaded,
    };
  },
};
</script>

<style scoped>
/* Fixed background that doesn't scroll */
.page-container {
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #e0f2fe 100%);
  background-attachment: fixed;
}

/* Header icon container */
.header-icon-container {
  background: linear-gradient(135deg, #3b82f6 0%, #9333ea 100%);
  padding: 0.75rem;
  border-radius: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Create button styling */
.create-button {
  background: linear-gradient(135deg, #3b82f6 0%, #9333ea 100%) !important;
  border: none !important;
  padding: 0.75rem 1.5rem !important;
  border-radius: 0.75rem !important;
  font-weight: 600 !important;
  color: white !important;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1) !important;
  transition: all 0.3s ease !important;
}

.create-button:hover {
  transform: translateY(-2px) !important;
  box-shadow: 0 8px 15px -3px rgba(0, 0, 0, 0.2) !important;
}

/* Table header section */
.table-header-section {
  background: #f8fafc;
  padding: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

/* Statistics header section */
.statistics-header-section {
  background: #f0fdf4;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #dcfce7;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

/* Drag and drop styling */
.datatable-row {
  background: white;
  border-bottom: 1px solid #f1f5f9;
  transition: all 0.2s ease;
  cursor: move;
}

.datatable-row:hover {
  background: #f8fafc;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

  .row-content {
    display: grid;
    grid-template-columns: 50px 1fr 200px 150px 100px 150px 120px 120px 120px;
    gap: 1rem;
    align-items: center;
    padding: 1.25rem 1.5rem;
  }

.drag-handle {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  cursor: grab;
}

.drag-handle:active {
  cursor: grabbing;
}

/* Ghost and chosen classes for drag and drop */
:deep(.ghost-row) {
  opacity: 0.5;
  background: #e5e7eb;
}

:deep(.chosen-row) {
  background: #dbeafe;
  box-shadow: 0 8px 25px rgba(59, 130, 246, 0.15);
}

/* Status badges */
.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  white-space: nowrap;
}

.status-enabled {
  background: #dcfce7;
  color: #166534;
}

.status-disabled {
  background: #fef2f2;
  color: #dc2626;
}

.target-badge {
  background: #dbeafe;
  color: #1e40af;
}

.time-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.5rem;
  background: #fef3c7;
  color: #92400e;
  border-radius: 0.375rem;
  font-size: 0.75rem;
  font-weight: 500;
}

.duration-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.5rem;
  background: #e0e7ff;
  color: #3730a3;
  border-radius: 0.375rem;
  font-size: 0.75rem;
  font-weight: 500;
}

.stat-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.5rem;
  border-radius: 0.375rem;
  font-size: 0.75rem;
  font-weight: 500;
  white-space: nowrap;
}

.shows-badge {
  background: #dbeafe;
  color: #1e40af;
}

.clicks-badge {
  background: #dcfce7;
  color: #166534;
}

.ctr-badge {
  background: #fef3c7;
  color: #92400e;
}

.location-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.5rem;
  background: #f3f4f6;
  color: #374151;
  border-radius: 0.375rem;
  font-size: 0.75rem;
  font-weight: 500;
}

.location-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.radius-info {
  font-size: 0.75rem;
  color: #6b7280;
}

.no-location {
  display: flex;
  align-items: center;
}

/* Ad video styling */
.ad-video-container {
  width: 60px;
  height: 60px;
  border-radius: 0.5rem;
  overflow: hidden;
  flex-shrink: 0;
  background: #000;
}

.ad-video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  cursor: pointer;
}

.ad-video-placeholder {
  width: 100%;
  height: 100%;
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
}

/* Action buttons */
.action-button {
  border: none !important;
  border-radius: 0.5rem !important;
  padding: 0.5rem !important;
  transition: all 0.2s ease !important;
}

.edit-button {
  background: #3b82f6 !important;
  color: white !important;
}

.edit-button:hover {
  background: #2563eb !important;
  transform: translateY(-1px) !important;
  box-shadow: 0 4px 8px rgba(59, 130, 246, 0.3) !important;
}

.delete-button {
  background: #dc2626 !important;
  color: white !important;
}

.delete-button:hover {
  background: #b91c1c !important;
  transform: translateY(-1px) !important;
  box-shadow: 0 4px 8px rgba(220, 38, 38, 0.3) !important;
}

/* Custom styling for modern datatable */
:deep(.modern-datatable) {
  border: none;
  border-radius: 0;
}

/* Responsive improvements */
@media (max-width: 1200px) {
  .row-content {
    grid-template-columns: 50px 1fr 150px 120px 80px 120px 100px 100px 100px;
    gap: 0.75rem;
    padding: 1rem;
  }
}

@media (max-width: 768px) {
  .row-content {
    grid-template-columns: 40px 60px 1fr;
    gap: 0.5rem;
    padding: 0.75rem;
  }
  
  .locations-cell,
  .target-cell,
  .display-time-cell,
  .statistics-cell,
  .avg-duration-cell,
  .status-cell,
  .actions-cell {
    display: none;
  }
  
  .max-w-7xl {
    max-width: 100%;
  }
  
  .p-6 {
    padding: 1rem;
  }
  
  .status-badge {
    padding: 0.375rem 0.75rem;
    font-size: 0.875rem;
  }
}

/* Ensure proper shadow styling */
.shadow-1 {
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
}
</style> 